/**
 * Guide Me — host wrapper.
 *
 * The React components live in `@empowernow/guide-me-react`. This file
 * is the single integration point for the EmpowerID Identity Fabric host: it gathers
 * the host-bound dependencies (auth, router, plugin context, locale,
 * PDP, tour catalog, plugin → guide URL mapping) and feeds them to the
 * package's `<GuideMeProvider>`. Then it renders the package's
 * `<GuideMeRoot>` which orchestrates the launcher / menu / player.
 *
 * Locator vocabularies are registered as a side-effect by importing
 * `./vocabularies` once before the provider mounts.
 */

import { useMemo, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  GuideMeProvider,
  GuideMeRoot as PackageGuideMeRoot,
  type GuideMeAdapter,
} from '@empowernow/guide-me-react';
import { useI18n } from '../contexts/LocalizationContext';
import { useAuth } from '@empowernow/bff-auth-react';
import { useOptionalAuthZ } from '../core/auth/authz-context';
import { useActivePlugin } from './useActivePlugin';
import {
  findTourFor,
  getGuideEntry,
  getRouteGuideUrl,
  getToursForPlugin,
  buildGuideUrl,
} from './pluginGuideMap';
import {
  loadTourById,
  preloadPlugin,
  preloadPluginSilent,
  getCachedTour,
  type TourSummary,
} from './tourCatalog';
import { searchTours, ensureNarrationIndex } from './searchTours';
// Side-effect import: registers every plugin's locator vocabulary
// with the runtime registry before any tour resolves an alias.
import './vocabularies';

function readCompileTimeFlags(): Record<string, boolean> {
  const flags: Record<string, boolean> = {};
  const env = (import.meta as unknown as { env?: Record<string, unknown> }).env ?? {};
  for (const key of Object.keys(env)) {
    if (!key.startsWith('VITE_FEATURE_')) continue;
    const name = key.slice('VITE_FEATURE_'.length).toLowerCase();
    const v = env[key];
    flags[name] = v === true || v === 'true' || v === '1' || v === 1;
  }
  return flags;
}

function useGuideMeHostAdapter(): GuideMeAdapter {
  const { pluginId, pathname, routeKey } = useActivePlugin();
  const { resolvedLocale, t } = useI18n();
  const navigate = useNavigate();
  const { user: authUser } = useAuth();
  const pdp = useOptionalAuthZ();

  return useMemo<GuideMeAdapter>(() => {
    const env = (import.meta as unknown as { env?: Record<string, unknown> }).env ?? {};
    const mode = (env.MODE as string | undefined) ?? 'production';
    const user = authUser
      ? {
          id: authUser.id,
          email: authUser.email,
          name: authUser.name ?? '',
          arn: authUser.arn ?? '',
        }
      : null;
    return {
      user,
      pathname,
      navigate: (to: string) => navigate(to),
      pluginId,
      routeKey,
      resolvedLocale,
      t: (key: string, opts?: { fallback?: string }) =>
        // The host's `useI18n().t` accepts a key and optional fallback;
        // adapt the call shape so the package can stay agnostic.
        (t as unknown as (k: string, o?: { fallback?: string }) => string)(key, opts),
      loadTourById,
      preloadPlugin,
      preloadPluginSilent,
      getCachedTour: (id: string) => getCachedTour(id) ?? null,
      getToursForPlugin: (id: string | null): ReadonlyArray<TourSummary> =>
        getToursForPlugin(id),
      findTourFor: (id: string | null, p: string) => findTourFor(id, p) ?? null,
      searchTours: (q, o) => searchTours(q, o),
      prepareSearch: () => ensureNarrationIndex(),
      getGuideEntry: (id: string | null) => getGuideEntry(id) ?? null,
      getRouteGuideUrl: (entry, p) => getRouteGuideUrl(entry as never, p),
      buildGuideUrl: (path) => buildGuideUrl(path),
      authorize: pdp ? (resourceType, action) => pdp.authorize(resourceType, action) : undefined,
      tourTopLevel: {
        env: mode === 'development' ? 'dev' : mode === 'production' ? 'prod' : mode,
        route: pathname,
        plugin: pluginId ?? '',
        flags: readCompileTimeFlags(),
        user: user ?? {},
        tenant: {},
      },
    };
  }, [authUser, navigate, pathname, pluginId, routeKey, resolvedLocale, t, pdp]);
}

export interface GuideMeShellProps {
  readonly children: ReactNode;
}

/**
 * Provides {@link GuideMeProvider} around host chrome so children (header,
 * main, etc.) can consume Guide Me hooks; mounts the package root last for
 * correct overlay stacking.
 */
export function GuideMeShell({ children }: GuideMeShellProps) {
  const adapter = useGuideMeHostAdapter();
  return (
    <GuideMeProvider adapter={adapter}>
      {children}
      <PackageGuideMeRoot />
    </GuideMeProvider>
  );
}

/** @deprecated Prefer {@link GuideMeShell} wrapping your layout; kept for callers that only need overlays. */
export function GuideMeRoot() {
  return (
    <GuideMeShell>
      {null}
    </GuideMeShell>
  );
}
